/**
 * features/communications/useParentNotices.ts
 * ------------------------------------------------------------------
 * Live feed of broadcast notices addressed to ONE parent, plus that
 * parent's read state.
 *
 * Query (gotcha #5): filter by the logged-in parent's AUTH uid —
 *   where('recipientParentUids', 'array-contains', uid)
 * The uid passed in must be the auth uid (AuthContext sets
 * userData.uid == auth uid). Do NOT combine the array-contains with
 * orderBy (gotcha #2 — that needs a composite index); we sort
 * newest-first in JS. The onSnapshot error callback console.warns
 * rather than silently flipping loading off (gotcha #3).
 *
 * Read state: each notice carries a `reads/{uid}` receipt. A parent
 * only ever has a handful of notices, so we lazily getDoc the receipt
 * for any notice we haven't classified yet, and markAsRead writes the
 * receipt (merge) — which also powers the staff "seen by X of Y" view.
 * ------------------------------------------------------------------
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';
import {
  PARENT_NOTICES_COLLECTION,
  PARENT_NOTICE_READS_SUBCOLLECTION,
  type ParentNotice,
} from '@/lib/parentNotices';

export interface UseParentNotices {
  notices: ParentNotice[];
  loading: boolean;
  unreadCount: number;
  isRead: (id: string) => boolean;
  markAsRead: (id: string) => Promise<void>;
}

export function useParentNotices(
  uid: string | undefined,
  parentName?: string,
): UseParentNotices {
  const [notices, setNotices] = useState<ParentNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  /* ---- live notices for this parent ---- */
  useEffect(() => {
    if (!uid) {
      setNotices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      query(
        collection(db, PARENT_NOTICES_COLLECTION),
        where('recipientParentUids', 'array-contains', uid),
      ),
      (snap) => {
        const list = snap.docs.map(
          (d) => ({ id: d.id, ...(d.data() as object) }) as ParentNotice,
        );
        // Newest first — sorted in JS (no orderBy in the query).
        list.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
        setNotices(list);
        setLoading(false);
      },
      (err) => {
        // Never swallow: a permission-denied / missing-index here looks
        // like a code bug but isn't (gotcha #3, #4).
        console.warn('[parent-notices] feed subscription error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [uid]);

  /* ---- lazily learn which notices are already read ---- */
  useEffect(() => {
    if (!uid || notices.length === 0) return;
    const unknown = notices.filter((n) => !readIds.has(n.id));
    if (unknown.length === 0) return;
    let cancelled = false;
    (async () => {
      const found: string[] = [];
      await Promise.all(
        unknown.map(async (n) => {
          try {
            const snap = await getDoc(
              doc(
                db,
                PARENT_NOTICES_COLLECTION,
                n.id,
                PARENT_NOTICE_READS_SUBCOLLECTION,
                uid,
              ),
            );
            if (snap.exists()) found.push(n.id);
          } catch (e) {
            console.warn('[parent-notices] read-state check failed:', e);
          }
        }),
      );
      if (!cancelled && found.length) {
        setReadIds((prev) => {
          const next = new Set(prev);
          found.forEach((id) => next.add(id));
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // readIds intentionally excluded: we only ever ADD to it, and a
    // stale (smaller) set just re-checks an already-read notice, which
    // is an idempotent getDoc. Including it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, notices]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!uid || readIds.has(id)) return;
      // Optimistic — flip locally first so the UI responds instantly.
      setReadIds((prev) => new Set(prev).add(id));
      try {
        await setDoc(
          doc(
            db,
            PARENT_NOTICES_COLLECTION,
            id,
            PARENT_NOTICE_READS_SUBCOLLECTION,
            uid,
          ),
          { readAt: new Date().toISOString(), parentName: parentName ?? '' },
          { merge: true },
        );
      } catch (e) {
        console.warn('[parent-notices] markAsRead failed:', e);
      }
    },
    [uid, parentName, readIds],
  );

  const unreadCount = useMemo(
    () => notices.filter((n) => !readIds.has(n.id)).length,
    [notices, readIds],
  );
  const isRead = useCallback((id: string) => readIds.has(id), [readIds]);

  return { notices, loading, unreadCount, isRead, markAsRead };
}
